import { useMutation, useQueryClient } from 'react-query';
import { authFetch } from '../../../../CommonUtils/authFetch';
import { httpBodyOptions } from '../../../../CommonUtils/httpBodyOptions';
import { queryKeys } from './queryKeys';
import { ExternalSystemId, ModelDTO, ModelGroupDTO, ModelGroupsDTO } from './useModelGroupsQuery';

type Model = Omit<ModelDTO, 'id'> & { id: string | null };

type UpsertModelGroupParams = Omit<Partial<ModelGroupDTO>, 'groupId' | 'modelPortfolios'> & {
  groupId?: string;
  modelPortfolios: Model[];
};

const upsertModelGroup = (modelGroup: UpsertModelGroupParams) => {
  return authFetch(
    modelGroup.externalSystem === ExternalSystemId.Orion
      ? '/Adviser/OrionModelImport/AddOrUpdateGroup'
      : '/Adviser/ModelPortfoliosGroups/AddOrUpdateGroup',
    {
      method: 'POST',
      ...httpBodyOptions(modelGroup, true),
    },
  );
};

export const useUpsertModelGroupMutation = () => {
  const queryClient = useQueryClient();

  const modelGroupsQK = queryKeys.modelGroups();

  return useMutation(upsertModelGroup, {
    onSuccess(modelGroup: ModelGroupDTO, { groupId }) {
      queryClient.setQueryData<ModelGroupsDTO | undefined>(modelGroupsQK, old => {
        if (old && old.modelPortfoliosGroups) {
          // We are updating a group
          if (groupId) {
            return {
              ...old,
              modelPortfoliosGroups: old.modelPortfoliosGroups.map(_modelGroup => {
                if (_modelGroup.groupId === groupId) {
                  return modelGroup;
                }

                return _modelGroup;
              }),
            };
          }

          // We are adding a new group ➜ need to sort alphabetically an keep the
          // Riskalyze group first in the order

          // Sort groups alphabetically by group name
          const sortedModelGroups = [...old.modelPortfoliosGroups, modelGroup].sort((a, b) =>
            a.groupName.localeCompare(b.groupName),
          );

          const riskalyzeGroupIndex = sortedModelGroups.findIndex(
            ({ externalSystem }) => externalSystem === ExternalSystemId.Riskalyze,
          );

          // If we have a riskalyze group and it is not in the first position already,
          // move it there.
          if (riskalyzeGroupIndex > 0) {
            const [riskalyzeGroup] = sortedModelGroups.splice(riskalyzeGroupIndex, 1);

            // Add the Riskalyze group to the first position
            sortedModelGroups.splice(0, 0, riskalyzeGroup);
          }

          return {
            ...old,
            modelPortfoliosGroups: sortedModelGroups,
          };
        }

        return old;
      });

      queryClient.invalidateQueries(queryKeys.modelGroups());
    },
  });
};
